import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import JobDialog from "@/components/JobDialog";
import { Mail, Plus, Check, RefreshCw, Inbox, Sparkles, Loader2, Link2, X } from "lucide-react";
import { fmtDate as fmtJobDate } from "@/lib/helpers";

const timeAgo = (iso) => {
  const diff = (Date.now() - new Date(iso).getTime()) / 36e5;
  if (diff < 1) return `${Math.max(1, Math.round(diff * 60))}m ago`;
  if (diff < 24) return `${Math.round(diff)}h ago`;
  return `${Math.round(diff / 24)}d ago`;
};

export default function EmailsPage() {
  const [emails, setEmails] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [converting, setConverting] = useState(null);

  const load = useCallback(async () => {
    const [em, ac, jb] = await Promise.all([api.get("/emails"), api.get("/email-accounts"), api.get("/jobs")]);
    setEmails(em.data); setAccounts(ac.data); setJobs(jb.data);
    try { const u = await api.get("/users"); setUsers(u.data); } catch (e) { setUsers([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addAccount = async (email, name) => {
    setAddOpen(false);
    try {
      await api.post("/email-accounts", { email, name });
      toast.success(`${email} added to your unified inbox`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add this email");
    }
  };

  const setConnected = async (account, connected) => {
    try {
      await api.patch(`/email-accounts/${encodeURIComponent(account.email)}`, { connected });
      if (!connected && filter === account.email) setFilter("all");
      toast.success(connected ? `${account.email} reconnected` : `${account.email} disconnected — past emails are kept`);
      await load();
    } catch (e) {
      toast.error("Could not update this email account");
    }
  };

  const jobMap = Object.fromEntries(jobs.map((j) => [j.id, j]));

  const accountEmails = new Set(accounts.map((a) => (a.email || "").toLowerCase()));
  const availableUsers = users.filter((u) => u.email && !accountEmails.has(u.email.toLowerCase()));

  const filtered = filter === "all" ? emails : emails.filter((e) => e.account === filter);
  const unread = emails.filter((e) => !e.is_read).length;

  const markRead = async (email) => {
    if (email.is_read) return;
    setEmails((prev) => prev.map((e) => (e.id === email.id ? { ...e, is_read: true } : e)));
    try { await api.patch(`/emails/${email.id}`, { is_read: true }); } catch (e) { console.error("Failed to mark email read:", e); }
  };

  const toggleJobLink = async (email, jobId) => {
    const current = email.job_ids || [];
    const next = current.includes(jobId) ? current.filter((id) => id !== jobId) : [...current, jobId];
    setEmails((prev) => prev.map((e) => (e.id === email.id ? { ...e, job_ids: next } : e)));
    try {
      await api.patch(`/emails/${email.id}`, { job_ids: next });
    } catch (e) {
      toast.error("Could not update job link");
      setEmails((prev) => prev.map((em) => (em.id === email.id ? { ...em, job_ids: current } : em)));
    }
  };

  const createJobFromEmail = async (email) => {
    setConverting(email.id);
    try {
      const res = await api.post("/voice/parse-text", { text: `${email.subject}. ${email.body}` });
      setActiveJob({ ...res.data.parsed, source: "email", notes: `${res.data.parsed.notes || ""}\n\nFrom email: ${email.from_name} <${email.from_email}>`.trim() });
      setDialogOpen(true);
      setEmails((prev) => prev.map((e) => (e.id === email.id ? { ...e, is_read: true, converted_to_job: true } : e)));
      api.patch(`/emails/${email.id}`, { is_read: true, converted_to_job: true }).catch(() => {});
    } catch (e) {
      toast.error("Could not extract job from this email.");
    } finally {
      setConverting(null);
    }
  };

  const connectGmail = () => {
    toast.info("Live Gmail sync is coming soon", {
      description: "Real-time Gmail import will connect here. You're currently viewing imported demo inboxes.",
    });
  };

  return (
    <div className="space-y-6 fade-up">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-600 mb-1">Unified Inbox</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Emails</h1>
          <p className="text-slate-500 mt-1">All enquiries from your connected Gmail accounts, in one place.</p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <Popover open={addOpen} onOpenChange={setAddOpen}>
            <PopoverTrigger asChild>
              <Button data-testid="add-email-btn" variant="outline" className="border-slate-300 rounded-lg">
                <Plus className="h-4 w-4 mr-1.5" /> Add Email
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 px-2 py-1.5">Add a feed from your users</p>
              {availableUsers.length === 0 ? (
                <p className="text-sm text-slate-400 px-2 py-3">All user emails are already added. Add people on the Users page first.</p>
              ) : (
                <div className="max-h-64 overflow-y-auto">
                  {availableUsers.map((u) => (
                    <button
                      key={u.user_id || u.email}
                      data-testid={`add-email-option-${u.email}`}
                      onClick={() => addAccount(u.email, u.name)}
                      className="w-full text-left px-2 py-2 rounded-md hover:bg-slate-100 flex items-center gap-2 transition-colors"
                    >
                      <Mail className="h-4 w-4 text-slate-400 shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-sm text-slate-800 truncate">{u.email}</span>
                        {u.name && <span className="block text-xs text-slate-400 truncate">{u.name}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </PopoverContent>
          </Popover>
          <Button data-testid="connect-gmail-btn" onClick={connectGmail} className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Connect Gmail
          </Button>
        </div>
      </div>

      {/* Account filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setFilter("all")}
          data-testid="email-filter-all"
          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${filter === "all" ? "bg-lime-400 border-lime-400 text-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}
        >
          All Inboxes {unread > 0 && <span className="ml-1 text-xs">({unread})</span>}
        </button>
        {accounts.map((a) => (
          <div
            key={a.email}
            data-testid={`email-account-${a.email}`}
            className={`inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full text-sm font-medium border transition-all ${!a.connected ? "bg-slate-50 border-slate-200 text-slate-400" : filter === a.email ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-600"}`}
          >
            <button
              onClick={() => a.connected && setFilter(a.email)}
              data-testid={`email-filter-${a.email}`}
              disabled={!a.connected}
              className="inline-flex items-center gap-1.5"
            >
              <span className={`h-2 w-2 rounded-full ${a.connected ? "bg-lime-500" : "bg-slate-300"}`} />
              {a.email}
              {!a.connected && <span className="text-[10px] uppercase tracking-wide">(off)</span>}
            </button>
            {a.connected ? (
              <button
                onClick={() => setConnected(a, false)}
                data-testid={`disconnect-email-${a.email}`}
                title="Disconnect — stops this address feeding the inbox. Past emails are kept."
                className="ml-0.5 p-1 rounded-full hover:bg-slate-200/40"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button
                onClick={() => setConnected(a, true)}
                data-testid={`reconnect-email-${a.email}`}
                title="Reconnect this feed"
                className="ml-0.5 p-1 rounded-full text-lime-600 hover:bg-lime-50"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Email list */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
          <Inbox className="h-10 w-10 text-slate-300 mx-auto" />
          <p className="mt-3 text-slate-500">No emails in this inbox.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="email-list">
          {filtered.map((email) => (
            <div
              key={email.id}
              onClick={() => markRead(email)}
              data-testid={`email-${email.id}`}
              className={`bg-white border rounded-xl p-4 transition-all hover:shadow-md cursor-pointer ${email.is_read ? "border-slate-200" : "border-lime-300 bg-lime-50/40"}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {!email.is_read && <span className="h-2 w-2 rounded-full bg-lime-500 shrink-0" />}
                    <span className="font-heading font-semibold text-slate-900 truncate">{email.from_name}</span>
                    <span className="text-xs text-slate-400 truncate">&lt;{email.from_email}&gt;</span>
                    {email.converted_to_job && (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0 text-[10px]">
                        <Check className="h-3 w-3 mr-0.5" /> Job created
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm font-medium text-slate-800 mt-1 truncate">{email.subject}</p>
                  <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{email.snippet}</p>
                  <p className="text-xs text-slate-400 mt-2">to {email.account} · {timeAgo(email.received_at)}</p>

                  {/* Linked jobs */}
                  <div className="flex flex-wrap items-center gap-1.5 mt-3" onClick={(e) => e.stopPropagation()}>
                    {(email.job_ids || []).map((jid) => (
                      jobMap[jid] ? (
                        <Badge key={jid} data-testid={`email-job-badge-${email.id}-${jid}`} className="bg-slate-900 text-white hover:bg-slate-800 border-0 gap-1 pr-1">
                          <Link2 className="h-3 w-3" /> {jobMap[jid].title}
                          <button onClick={() => toggleJobLink(email, jid)} className="ml-0.5 hover:text-lime-400"><X className="h-3 w-3" /></button>
                        </Badge>
                      ) : null
                    ))}
                    <Popover>
                      <PopoverTrigger asChild>
                        <button data-testid={`assign-job-btn-${email.id}`} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border border-dashed border-slate-300 text-slate-500 hover:border-lime-400 hover:text-lime-700 transition-all">
                          <Plus className="h-3 w-3" /> Link to job
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-80 p-0" onClick={(e) => e.stopPropagation()}>
                        <div className="px-3 py-2 border-b border-slate-100">
                          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Assign to job(s)</p>
                          <p className="text-[11px] text-slate-400">Link this email to one or more jobs</p>
                        </div>
                        <div className="max-h-72 overflow-y-auto py-1">
                          {jobs.map((j) => {
                            const linked = (email.job_ids || []).includes(j.id);
                            return (
                              <button
                                key={j.id}
                                data-testid={`assign-option-${email.id}-${j.id}`}
                                onClick={() => toggleJobLink(email, j.id)}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50 transition-colors"
                              >
                                <span className={`h-4 w-4 rounded flex items-center justify-center border shrink-0 ${linked ? "bg-lime-500 border-lime-500" : "bg-white border-slate-300"}`}>
                                  {linked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                </span>
                                <span className="min-w-0">
                                  <span className="text-sm text-slate-800 block truncate">{j.title}</span>
                                  <span className="text-[11px] text-slate-400 block truncate">{j.client_name} · {fmtJobDate(j.start)}</span>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div className="shrink-0">
                  <Button
                    size="sm"
                    data-testid={`create-job-from-email-${email.id}`}
                    onClick={(e) => { e.stopPropagation(); createJobFromEmail(email); }}
                    disabled={converting === email.id}
                    className="bg-lime-400 hover:bg-lime-300 text-slate-900 font-medium"
                  >
                    {converting === email.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <><Sparkles className="h-4 w-4 mr-1" /> Create Job</>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <JobDialog open={dialogOpen} onOpenChange={setDialogOpen} job={activeJob} />
    </div>
  );
}
